'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import {
  codebaseReposQueryKeys,
  useSyncCodebaseRepos,
} from '@/hooks/useCodebaseRepos';
import { unifiedCodebaseQueryKeys } from '@/hooks/useUnifiedCodebaseManagement';
import { useInstallationSyncStatus } from '@/hooks/useInstallationSyncStatus';
import { useUserLogged } from '@/hooks/useUserLogged';

interface OnboardingRepoSyncGateProps {
  orgUUID: string;
  userUUID?: string;
  /**
   * The step's existing skeleton/loader, shown while the sync runs. When
   * omitted, a default repo-list-shaped skeleton is rendered.
   */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/** Repo-list-shaped placeholder: same card shell + row rhythm as the real lists. */
function RepoListSkeleton() {
  return (
    <div className="rounded-lg border border-ds-border-subtlest bg-ds-surface-white overflow-hidden">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 p-3 border-b border-ds-border-subtlest last:border-b-0"
        >
          <Skeleton className="h-4 w-4 rounded" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * Orgs whose listing has already been primed this page session, by a provider→DB
 * sync or, on GitHub, by a refetch. Survives Dialog unmount/remount so Choose
 * Repositories (and other steps) don't re-prime every time the modal opens.
 */
const syncedOrgsThisSession = new Set<string>();

/**
 * Ceiling on how long the gate waits for the backend's per-repo installation
 * processing. Past this the step renders anyway (the DB usually already holds
 * most repos) and the listing still refetches when processing finishes.
 */
const INSTALL_WAIT_CEILING_MS = 90_000;

/**
 * Age past which an IN_PROGRESS row is treated as dead rather than live.
 *
 * The backend writes COMPLETED/FAILED inline after its worker pool, not in a
 * `finally` (`pr_reviewer/handler.py:7190`), so a raise or a killed task leaves
 * the org stuck at IN_PROGRESS permanently. Without this guard that one row
 * would re-block every gated step on every page load for that org.
 */
const INSTALL_STALE_AFTER_MS = 10 * 60_000;

/**
 * Blocks an onboarding step behind a one-shot provider→DB repo sync.
 *
 * Onboarding is the first time an org lists repos, so the DB may be empty or
 * stale; this gate primes the listing on mount (once per org per page session),
 * streams a live "N repositories found" count, and renders the step only when
 * priming finishes — so the user sees the complete list, not a partial one.
 *
 * GitHub primes with a refetch, not a sync. `listCodebaseRepos/` returns
 * RepositoryModel unioned with PRBotInstallationModel, and the App installation
 * webhook writes the latter, so every installed repo is already listable. A
 * GitHub sync makes no provider calls at all, it only copies rows between those
 * two tables, and the onboarding steps address repos by name rather than by
 * RepoUUID. GitLab and Bitbucket still sync: the crawl is their only way to
 * discover a repo. An org whose provider has not resolved keeps the sync, since
 * a needless crawl is cheaper than an empty repo list.
 *
 * The sync finishing is not the same as the org being ready: the App install
 * processes repos one by one on the backend, so the gate also waits while
 * `getInstallationSyncStatus/` reports IN_PROGRESS and refetches the listings
 * once it leaves that state. Without this, a step can render a correct-but-
 * premature count (0 repos) and only repair itself on a later refetch.
 *
 * Never a dead end: on sync error the step renders anyway (the DB may already
 * hold repos) and the sync hook surfaces a non-blocking error toast. The same
 * applies to installation processing, which is also capped by
 * INSTALL_WAIT_CEILING_MS.
 */
export default function OnboardingRepoSyncGate({
  orgUUID,
  userUUID = '',
  fallback,
  children,
}: OnboardingRepoSyncGateProps) {
  // No success toast — the list appearing IS the completion feedback.
  const { sync, liveCount } = useSyncCodebaseRepos({ toasts: 'error-only' });
  const [done, setDone] = useState(() =>
    Boolean(orgUUID && syncedOrgsThisSession.has(orgUUID))
  );
  const firedRef = useRef(false);
  const queryClient = useQueryClient();
  const { status: installStatus, progressData } =
    useInstallationSyncStatus(orgUUID);
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  const wasProcessingRef = useRef(false);
  const { user: loggedUser, organization: loggedOrg } = useUserLogged();
  // Deliberately no `?? 'github'` fallback: an unresolved provider must fall
  // through to the sync, which is correct for every provider, rather than skip
  // the only discovery path GitLab and Bitbucket have.
  const isGithubProvider =
    (loggedOrg?.Source ?? loggedUser?.provider ?? '')
      .toString()
      .toLowerCase() === 'github';

  useEffect(() => {
    if (!orgUUID) return;
    if (syncedOrgsThisSession.has(orgUUID)) {
      setDone(true);
      return;
    }
    if (firedRef.current) return;
    firedRef.current = true;

    const primed = () => {
      syncedOrgsThisSession.add(orgUUID);
      setDone(true);
    };

    if (isGithubProvider) {
      // Same invalidation the sync hook performs on success, minus the crawl.
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: codebaseReposQueryKeys.all,
        }),
        queryClient.invalidateQueries({
          queryKey: unifiedCodebaseQueryKeys.all,
        }),
      ]).finally(primed);
      return;
    }

    void sync(orgUUID, userUUID).finally(primed);
  }, [orgUUID, userUUID, sync, isGithubProvider, queryClient]);

  useEffect(() => {
    const timer = setTimeout(
      () => setWaitedTooLong(true),
      INSTALL_WAIT_CEILING_MS
    );
    return () => clearTimeout(timer);
  }, []);

  // Repos land in the DB as the backend works through the installation, so the
  // listings are stale until it stops. Refetch on the way out of IN_PROGRESS,
  // including when the ceiling already revealed the step.
  useEffect(() => {
    if (installStatus === 'IN_PROGRESS') {
      wasProcessingRef.current = true;
      return;
    }
    if (!wasProcessingRef.current) return;
    wasProcessingRef.current = false;
    void queryClient.invalidateQueries({
      queryKey: codebaseReposQueryKeys.all,
    });
  }, [installStatus, queryClient]);

  // Org not resolved yet — let the step handle its own loading.
  if (!orgUUID) return <>{children}</>;

  const startedAtMs = progressData?.started_at
    ? Date.parse(progressData.started_at)
    : NaN;
  const startedRecently =
    !Number.isFinite(startedAtMs) ||
    Date.now() - startedAtMs < INSTALL_STALE_AFTER_MS;
  const stillProcessing =
    installStatus === 'IN_PROGRESS' && !waitedTooLong && startedRecently;

  if (done && !stillProcessing) {
    // Soft reveal so the skeleton→content swap doesn't pop.
    return <div className="motion-fade">{children}</div>;
  }

  return (
    <div className="w-full">
      {fallback ?? <RepoListSkeleton />}
      <div
        className="mt-3 flex items-center justify-center gap-2 text-sm text-ds-text-subtle"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {stillProcessing ? (
          // No processed/total counter here: the backend writes progress_data
          // exactly twice (processed: 0 on start, final count on finish), so a
          // count would sit at "0 of N" for the whole wait. Show the repo total
          // being set up instead, which is known up front and does not lie.
          <span>
            Setting up
            {progressData?.total ? (
              <span className="tabular-nums text-ds-text-darker font-medium">
                {' '}
                {progressData.total}{' '}
                {progressData.total === 1 ? 'repository' : 'repositories'}
              </span>
            ) : (
              ' your repositories'
            )}
            …
          </span>
        ) : (
          <span>
            Fetching your repositories…
            {liveCount != null && (
              <span className="tabular-nums text-ds-text-darker font-medium">
                {' '}
                {liveCount} found
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
